import { Box, Icon, Paragraph } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';
import {
  type Attachment,
  ATTACHMENT_STATUS,
  DeleteAttachmentsButton,
  FILE_TYPES,
  filterAttachments,
  getChatBubbleBorderRadius,
  MessageDocumentList,
  MessageImageGrid,
} from '@zg-rentals/satellite-components';
import { useAuth } from '@zg-rentals/auth-comet';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ModalType } from '../../types/ModalType';
import useModal from '../../hooks/useModal';
import StringConstants from '../../constants/StringConstants';
import ConditionalWrapper from '../ConditionalWrapper';
import type { HandleOpenDeleteAttachmentsModal, HandleOpenImagesViewer } from '../MessageList';
import { logError } from '@zg-rentals/log-error';
import { getCurrentSessionURL } from '../../utils/fullStory';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import {
  emitIreneDownloadAttachmentsEvent,
  IRENE_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/attachment';
import { deletedAttachmentsContainerStyles } from './styles';

export type AttachmentsForMessage = Array<Attachment>;

type Props = {
  attachments: AttachmentsForMessage;
  isMessageOwner: boolean;
  isMessageSent: boolean;
  messageId: string;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
};

const MAX_THUMBNAIL_COUNT = 6;

type AttachmentsRenderError = {
  conversationId: string;
  loggerName: string;
};

export const logAttachmentsRenderError = ({ conversationId, loggerName }: AttachmentsRenderError) => {
  logError({
    error: new Error('logAttachmentsRenderError'),
    context: {
      fullStorySessionUrl: getCurrentSessionURL(),
      details: `${loggerName} Error rendering attachment, attachmentMetadataStatus is not complete or url is missing`,
      conversationId,
    },
  });
};

export default function MessageAttachments({
  attachments,
  isMessageOwner,
  isMessageSent,
  messageId,
  openDeleteAttachmentsModal,
  openImagesViewer,
}: Props) {
  const { conversationId } = useContext(InboxPageContext);
  const { user } = useAuth();
  const { setActiveModal } = useModal();
  const [shouldLogImageGridError, setShouldLogImageGridError] = useState(false);
  const [shouldLogNonImageError, setShouldLogNonImageError] = useState(false);

  const { imageAttachments, nonImageAttachments, hasEveryAttachmentExpired, hasSomeDeletedAttachments } = useMemo(
    () => filterAttachments(attachments, FILE_TYPES.IMAGE),
    [attachments],
  );

  useEffect(() => {
    if (shouldLogImageGridError) {
      logAttachmentsRenderError({
        conversationId: conversationId || '',
        loggerName: 'MessageAttachments_ImageThumbnailGrid',
      });
    }
  }, [shouldLogImageGridError, conversationId]);

  useEffect(() => {
    if (shouldLogNonImageError) {
      logAttachmentsRenderError({
        conversationId: conversationId || '',
        loggerName: 'MessageAttachments_NonImageList',
      });
    }
  }, [shouldLogNonImageError, conversationId]);

  const handleDownloadEventTracking = () => {
    emitIreneDownloadAttachmentsEvent(user);
  };

  const isDeleteIconDisabled = !isMessageSent || hasEveryAttachmentExpired;
  const hasImageAttachments = imageAttachments.length > 0;
  const hasNonImageAttachments = nonImageAttachments.length > 0;
  const hasAllDeletedAttachments = !hasImageAttachments && !hasNonImageAttachments;
  const shouldShowDeleteIcon = (isMessageOwner && !hasAllDeletedAttachments && !hasEveryAttachmentExpired) || false;

  // Create a mutated array to only pass down the max
  // images necessary for rendering in the thumbnail grid
  const imageAttachmentsSubset = [...imageAttachments];
  let overflowImagesCount = 0;
  if (imageAttachments.length > MAX_THUMBNAIL_COUNT) {
    overflowImagesCount = imageAttachments.length - MAX_THUMBNAIL_COUNT;
    imageAttachmentsSubset.length = MAX_THUMBNAIL_COUNT;
  }

  const handleImageThumbnailClick = (zDocId: string) => {
    const selectedImageIndex = imageAttachments.findIndex((imageAttachment) => imageAttachment.zDocId === zDocId);
    openImagesViewer({ imageAttachments, selectedImageIndex });
  };

  const handleOpenDeleteAttachmentsModal = () => {
    openDeleteAttachmentsModal({ messageId, attachments: [...imageAttachments, ...nonImageAttachments] });
  };

  const handleOpenAttachmentGuidelinesModal = () => {
    setActiveModal(ModalType.AttachmentModal);
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMessageOwner ? 'flex-end' : 'flex-start',
        width: '100%',
      }}
    >
      {hasSomeDeletedAttachments && (
        <Box
          css={{
            ...deletedAttachmentsContainerStyles,
            ...getChatBubbleBorderRadius(isMessageOwner),
          }}
        >
          <Icon
            css={{ color: 'border.action.neutral.default', fontSize: '1.4em', marginRight: 'default' }}
            render={<IconDeleteOutline />}
          />
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', maxWidth: '174px' }}>
            One or more attachments were removed for all parties
          </Paragraph>
        </Box>
      )}

      {hasImageAttachments && (
        <ConditionalWrapper
          shouldRenderWrapperElement={shouldShowDeleteIcon}
          wrapper={(children) => (
            <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
          )}
        >
          <>
            {shouldShowDeleteIcon && !hasNonImageAttachments && (
              <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
            )}

            <MessageImageGrid
              imageAttachments={imageAttachmentsSubset}
              isMessageOwner={isMessageOwner}
              onClick={handleImageThumbnailClick}
              overflowCount={overflowImagesCount}
              onLogError={() => setShouldLogImageGridError(true)}
              onExpiredDetailsClick={handleOpenAttachmentGuidelinesModal}
              impersonationDetectedValue={ATTACHMENT_STATUS.IMPERSONATION_DETECTED}
              impersonationTooltipText={StringConstants.TOOLTIP.IMPERSONATION_VIEW}
            />
          </>
        </ConditionalWrapper>
      )}

      {hasNonImageAttachments && (
        <ConditionalWrapper
          shouldRenderWrapperElement={shouldShowDeleteIcon}
          wrapper={(children) => (
            <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
          )}
        >
          <>
            {shouldShowDeleteIcon && (
              <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
            )}

            <MessageDocumentList
              isMessageOwner={isMessageOwner}
              nonImageAttachments={nonImageAttachments}
              onExpiredDetailsClick={handleOpenAttachmentGuidelinesModal}
              onEventTracking={handleDownloadEventTracking}
              onLogError={() => setShouldLogNonImageError(true)}
              downloadEventId={IRENE_DOWNLOAD_ATTACHMENTS_EVENT_TYPE_ID}
            />
          </>
        </ConditionalWrapper>
      )}
    </Box>
  );
}
